import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { apiClient } from "@/client";
import { mergedColumns } from "@/components/map-review/capability";
import {
	MAP_REVIEW_TABLES,
	RELATION_COLUMN,
	type MapReviewColumn,
	clientZoneOffsetMinutes,
	columnByKey,
} from "@/components/map-review/columns";
import {
	LEDGER_QUERY_PATH,
	OVER_RATE_CLAUSE,
	ledgerFilter,
	ledgerLocation,
	ledgerOrder,
	ledgerSelection as ledgerMapSelection,
	ledgerTZOffset,
	relatedSetClauses,
} from "@/components/map-review/ledgerQuery";
import { loadAnalysis, saveAnalysis } from "@/lib/mapReviewPrefs";
import {
	emptyMapReviewSpec,
	type MapReviewHydratedState,
} from "@/lib/mapReviewState";
import type { DrawMode } from "@/map/drawModes";
import { isRequestTimeout } from "@/sentry-report";
import type {
	MapReviewAskRequest,
	MapReviewAskResponse,
	MapReviewBulkSelection,
	MapReviewCapabilities,
	MapReviewCapability,
	MapReviewColumnFilter,
	MapReviewColumnSummary,
	MapReviewCounts,
	MapReviewDashboardRequest,
	MapReviewBufferCombine,
	MapReviewDashboardResponse,
	MapReviewFeatureCountsRequest,
	MapReviewFeatureCountsResponse,
	MapReviewHistogramRequest,
	MapReviewHistogramResponse,
	MapReviewSelection,
	MapReviewPlannedRevisitRequest,
	MapReviewPopulationRequest,
	MapReviewPopulationView,
	MapReviewQueryBody,
	MapReviewQueryBodyFilter,
	MapReviewQueryEnvelope,
	MapReviewRow,
	MapReviewSiteAssignmentClearRequest,
	MapReviewSiteAssignmentRequest,
	MapReviewSiteAssignmentResult,
	MapReviewSort,
	MapReviewSpec,
	MapReviewSummaryRequest,
	MapReviewSummaryResponse,
	MapReviewSurveillanceResponse,
	MapReviewTable,
	MapReviewTempZone,
	MapReviewTrapInstanceRow,
	MapReviewTrapVocabulary,
	MapReviewWasteBucket,
	MapReviewWasteRequest,
	MapReviewWasteView,
	SiteTagAssignRequest,
	SiteTagAssignResult,
} from "@/type/api";
import type {
	VectorSurvEquipment,
	VectorSurvFilingAnnotationRequest,
	VectorSurvFilingAnnotationResult,
} from "@/type/vectorsurv";

// The one filter spec the whole Map Review surface shares: the drawer and the
// map both read and write this store, so a filter set anywhere is in force
// everywhere, and the server resolves it once. The drawer's checkbox selection
// also lives here, per table, because a bulk write is that selection.

// Before the first load nothing is filtered, so the highlight reproduces the
// (empty) set exactly — `exact` starts true to keep the map from labelling
// itself approximate on an unfiltered page.
const emptySelection: MapReviewSelection = {
	ids: null,
	selection_token: null,
	exact: true,
	truncated: false,
	product_ids: null,
	start_min: null,
	start_max: null,
};

// Text-carrying mutations settle before they reach the server: a keystroke per
// POST would hammer a resolver whose scans cover 116k treatments.
const FILTER_DEBOUNCE_MS = 250;

// The ledger's height as a fraction of the map box it shares with the two tool
// rails. The drawer drags it, and the rails read the same number to end one
// gutter above the drawer's top edge, so it belongs here rather than in the
// drawer: one source for the drawer's pixels and the rails' bottom.
const DEFAULT_DRAWER_FRACTION = 0.33;
const MIN_DRAWER_FRACTION = 0.15;
const MAX_DRAWER_FRACTION = 1;

// The temporary zone a clicked bivariate class materialises. It is named for
// the class so the toolbar states what is filtering, and the prefix is what
// lets the store recognise — and replace — the population view's own zone
// among the operator's buffers.
const POPULATION_ZONE_PREFIX = "Population: ";

function isPopulationZone(zone: MapReviewTempZone): boolean {
	return zone.name.startsWith(POPULATION_ZONE_PREFIX);
}

// The temporary zone a clicked waste class materialises, and the prefix the
// store uses to recognise — and replace — the waste view's own zone among the
// operator's buffers. The population view keeps its own prefix, so the two
// questions' class zones cannot be mistaken for one another.
const WASTE_ZONE_PREFIX = "Waste: ";

/** One applied clause, as the Analysis pane's indicator reads it: a stable key,
 * the column or narrowing's own name, and the value in the operator's terms. */
export interface AppliedFilterSummary {
	key: string;
	label: string;
	detail: string;
}

/** One table's checkbox selection: every row matching the filter (`all`, minus
 * any the operator unticked), or an explicit set of row ids. */
export interface LedgerSelection {
	all: boolean;
	ids: number[];
	excluded: number[];
}

const EMPTY_LEDGER_SELECTION: LedgerSelection = {
	all: false,
	ids: [],
	excluded: [],
};

export const useStoreMapReview = defineStore("map-review", () => {
	// State
	// This visit's analysis is restored from sessionStorage, so a reload keeps
	// the operator's filter and view while a new visit starts from these
	// defaults. The restored spec is validated on load; a stale or malformed
	// one is discarded rather than sent to the server.
	const restored = loadAnalysis();
	const spec = ref<MapReviewSpec>(
		restored?.spec
			? {
					...restored.spec,
					// A search restored from an earlier visit cannot be shown or
					// cleared any more: the ledger's search box is gone, and text
					// search now finds records in the rail without narrowing the
					// tables. A search left in the spec would narrow the counts
					// with nothing on screen to explain or undo it.
					search: emptyMapReviewSpec().search,
				}
			: // A fresh visit narrows nothing: no clauses, no zone selection, no
				// buffers and no Feature membership.
				emptyMapReviewSpec(),
	);
	const activeTable = ref<MapReviewTable>(restored?.activeTable ?? "treatment");
	// The ledger tables THIS surface reads, in tab order. The review workbench
	// reads all nine of the registry's tables and pins them on its own setup; a
	// surface of its own — the lab's records view — pins the two it shows, so the
	// tab strip, the counts and the round of reads are the surface's rather than
	// the registry's. A ledger outside the set is simply not read, and
	// `activeTable` is kept inside it (setLedgerTables, setActiveTable and the
	// switches the links make below).
	const ledgerTables = ref<MapReviewTable[]>([...MAP_REVIEW_TABLES]);
	const selection = ref<{ table: MapReviewTable; id: number } | null>(null);
	const rows = ref<MapReviewRow[]>([]);
	const total = ref(0);
	const counts = ref<MapReviewCounts>({
		site: 0,
		feature: 0,
		treatment: 0,
		observation: 0,
		trap_station: 0,
		trap_instance: 0,
		species_count: 0,
		trap_sample: 0,
		zone: 0,
		pool: 0,
	});
	// The last successful query's per-column footer aggregates, keyed by column
	// key, read by the ledger's always-visible summary row. They describe the
	// whole filtered set the server resolved, so a page change replaces them
	// wholesale like the rows do — and a failed load leaves the previous reading
	// in place rather than blanking the footer under an error toast.
	const summary = ref<Record<string, MapReviewColumnSummary>>({});
	// The capability each ledger resource declares for its own columns (D6/D7),
	// read from `OPTIONS /api/<resource>` once per session. It is the server's
	// answer to "may this column be ordered, which operators does it take, which
	// members does its vocabulary hold", so the ledger sends what the server will
	// accept rather than what the presentation registry happens to say; a resource
	// whose document is missing keeps the registry's own declaration
	// (capability.ts).
	const capabilities = ref<MapReviewCapabilities>({});
	const mapSelection = ref<Record<MapReviewTable, MapReviewSelection>>({
		site: { ...emptySelection },
		feature: { ...emptySelection },
		treatment: { ...emptySelection },
		// The observation layer never narrows by an id set — the tile carries
		// exactly the resolved set — so its selection is the empty one until a
		// response fills in its token.
		observation: { ...emptySelection },
		trap_station: { ...emptySelection },
		trap_instance: { ...emptySelection },
		species_count: { ...emptySelection },
		// The sample is drawn at its instance's own captured point, so its
		// selection is the kept samples' own ids and starts empty.
		trap_sample: { ...emptySelection },
		// The zone layer is the `zone` tile map, so its selection is only the
		// kept ids and starts empty.
		zone: { ...emptySelection },
		// The pool layer carries no tile-side predicate either, so the pooled
		// rows' own ids are its whole highlight.
		pool: { ...emptySelection },
	});
	// The over-rate treatment ids the map toggle renders, and whether that set
	// is exact (false: truncated at the server's 20,000 cap).
	const overRateIDs = ref<number[]>([]);
	const overRateExact = ref(true);
	const page = ref(restored?.page ?? 0);
	const limit = ref(restored?.limit ?? 250);
	const sort = ref<MapReviewSort | null>(restored?.sort ?? null);
	// The server rejects a sort on a column the table does not carry, and this
	// ref is shared by every table — Sites and Features sort by `created`,
	// Treatments by `start_datetime`, Observations by `observed_at`. Carrying a
	// sort across a table switch therefore sent `observed_at` with the feature
	// table and the whole query was rejected: `sort on unknown feature column
	// "observed_at"`. Only a sort the active table can honour is sent; the rest
	// stays in the ref, so switching back restores it. What the table can honour
	// is the server's own answer — the capability document when it has arrived,
	// the presentation registry otherwise — so a generic `QUERY` never carries an
	// ordering its resource would refuse (422).
	const activeSort = computed<MapReviewSort | null>(() => {
		const s = sort.value;
		if (!s) return null;
		return columnsFor(activeTable.value).find((c) => c.key === s.column)
			?.sortable
			? s
			: null;
	});
	const loading = ref(false);
	const error = ref("");
	// The toast title paired with `error`. The table's own failures are the
	// common case, but a VectorSurv failure is not a table query and must not be
	// titled as one.
	const errorTitle = ref("");
	// The last ledger round's outcome. The counts are the server's answer to
	// "what is in the district under this spec", so they are only an answer in
	// the `resolved` state: a round that failed leaves the previous (or the
	// initial zero) counts in place, and the map's empty check must not read
	// those as an empty district. `failed` is what the surface offers the
	// retry from.
	const loadState = ref<"idle" | "loading" | "resolved" | "failed">("idle");
	// The deadline the failed round hit, in seconds, when its rejection was the
	// transport's own abort. `null` for every other failure: a server refusal
	// has its own message and must not be reported as a timeout.
	const timedOutSeconds = ref<number | null>(null);
	const drawerOpen = ref(restored?.drawerOpen ?? false);
	// Whether the active ledger's typed filter row (the controls under its column
	// labels) is showing. It lives here because the Filters tool in the right rail
	// toggles it now that the ledger has no header of its own; the drawer only
	// draws the row. Not persisted: the controls start folded so the table opens
	// with its rows.
	const filtersOpen = ref(false);

	// Prefer the message the API sent over axios's generic "Request failed with
	// status code N", which tells an operator nothing about what to do. The
	// response body is read by narrowing, not by asserting a shape onto it: an
	// assertion would read the field whatever the body actually was.
	function apiErrorMessage(err: unknown, fallback: string): string {
		if (err instanceof Error && "response" in err) {
			const response: unknown = err.response;
			if (response && typeof response === "object" && "data" in response) {
				const { data } = response;
				if (data && typeof data === "object" && "message" in data) {
					const { message } = data;
					if (typeof message === "string" && message !== "") return message;
				}
			}
		}
		if (err instanceof Error && err.message) return err.message;
		return fallback;
	}
	const drawerFraction = ref(
		restored?.drawerExpanded ? MAX_DRAWER_FRACTION : DEFAULT_DRAWER_FRACTION,
	);
	// Derived, not stored: "expanded" is exactly "the drawer is at the top", so
	// the drag and the expand control cannot disagree about it, and dragging off
	// the top un-expands the drawer as it happens.
	const drawerExpanded = computed(
		() => drawerFraction.value >= MAX_DRAWER_FRACTION,
	);
	// The ledger's checkbox selection, per table. The spec still decides which
	// rows the table, counts and map are showing; these are the rows the
	// operator ticked for a bulk write. Keeping one set per table is what lets
	// an operator mark Sites, look at Features, and come back without losing
	// the Sites — and it is why a selection is never mixed-type, so a write
	// always has one action vocabulary. `all` means every row matching the
	// current filter, with `excluded` holding the rows unticked afterwards;
	// otherwise `ids` is the explicit set. A spec change clears every table's
	// selection: the rows a set named no longer mean what the operator ticked.
	const ledgerSelections = ref<
		Partial<Record<MapReviewTable, LedgerSelection>>
	>({});

	// The mode an armed draw tool draws in (freehand, a circle from a centre and
	// a radius, or a clicked-vertex polygon). One ref rather than per-caller
	// state, so the Area filter and the zone draw cannot be armed in two
	// different modes; freehand is the default because it is what those tools
	// already did. Never persisted — a mode is a property of the draw in
	// progress, not of the visit.
	const drawMode = ref<DrawMode>("freehand");

	// The report's aggregate and its load state, for the Analysis pane. The
	// aggregate is fetched when that pane opens and refetched after a drill-down
	// — deliberately NOT live: the panels are a reading of the spec as it stood
	// when opened, and a filter set elsewhere while the operator reads them must
	// not silently redraw what they are looking at.
	const dashboard = ref<MapReviewDashboardResponse | null>(null);
	const dashboardLoading = ref(false);
	// Monotonic request id, exactly as `load` carries: a drill-down fired from
	// one panel can overlap the response to the previous fetch, and a stale
	// aggregate must be dropped whole rather than repainted over the newer one.
	let dashboardSeq = 0;

	// The surveillance section's own aggregate: the trap family's weekly
	// abundance against a five-year baseline, from `POST
	// /api/surveillance`. Independent of the dashboard aggregate: it
	// names no filter spec (the metric is the whole district's season), so unlike
	// `dashboard` it is not refetched after a drill-down. Like the dashboard it
	// is a mode-scoped read and is never persisted.
	const surveillance = ref<MapReviewSurveillanceResponse | null>(null);
	const surveillanceLoading = ref(false);
	// Monotonic request id, exactly as the others carry: opening the pane twice
	// can overlap two fetches, and a stale season must be dropped whole.
	let surveillanceSeq = 0;

	// The population view: the cell field, the stations with their weekly
	// series, the nine dissolved class geometries and the season chart set, all
	// under the shared spec (`POST /api/map-review/population`). It is the report's
	// question-specific payload rather than part of `dashboard`, because it
	// carries geometry the aggregate board never needs; the client renders what
	// the server counted and never resolves a clause or builds a class shape.
	// The Analysis pane is its caller — on mount and after every spec change —
	// so the read is not made by a surface that is not showing it.
	const population = ref<MapReviewPopulationView | null>(null);
	const populationLoading = ref(false);
	// Whether the bivariate cells are drawn on the workbench map. A mode, never
	// persisted: sending the view to the map is a reading of the rail, not a
	// filter.
	const populationOnMap = ref(false);
	// Whether the sent layer's fill is showing. It is the Layers pane's
	// checkbox, separate from `populationOnMap` so hiding the fill keeps the
	// payload on the map: the operator can bring it back without the rail
	// re-reading the view. A fresh send starts visible.
	const populationVisible = ref(true);
	// The bivariate class in force (0..8), or null. The filter itself is the
	// `Population: <class name>` temporary zone on the spec; this names it for
	// the legend and for the class click's own state.
	const populationClass = ref<number | null>(null);
	// The body last asked for and its promise. The pane can trigger the read
	// twice for one body — its mount racing its spec watch, or a class click
	// racing the watch the click's own spec change wakes — and one body must be
	// one request. A different body is always a new read.
	let populationKey = "";
	let populationRequest: Promise<void> | null = null;
	// Monotonic request id, exactly as the others carry: a class click while a
	// read is in flight can supersede its spec, and a stale field must be
	// dropped whole rather than repaint the newer one.
	let populationSeq = 0;

	// The waste view: the visit outcome split, the weekly band, the top Sites,
	// the re-treatment buckets with their Feature ids, the observers, and the
	// bivariate H3 cells (`POST /api/map-review/waste`). It is its own question's
	// payload rather than part of the dashboard, because the cells carry
	// geometry and the buckets carry member sets the aggregate board never
	// needs. The Analysis pane is its one caller — on mount and after every
	// spec change — so nothing reads it that is not showing it.
	const waste = ref<MapReviewWasteView | null>(null);
	const wasteLoading = ref(false);
	// Whether the waste cells are drawn on the workbench map. A mode, never
	// persisted.
	const wasteOnMap = ref(false);
	// Whether the sent layer's fill is showing, exactly as the population view
	// carries it: the Layers pane's checkbox, and a fresh send starts visible.
	const wasteVisible = ref(true);
	// The bivariate class in force (0..8), or null. The filter itself is the
	// `Waste: <class name>` temporary zone on the spec; this names it for the
	// legend and the class click's own state.
	const wasteClass = ref<number | null>(null);
	// The re-treatment bucket in force, or null. The filter itself is
	// `spec.feature_ids`; this names it so the panel can highlight the row and
	// clear it. It is cleared whenever the Feature selection leaves the spec.
	const wasteBucket = ref<string | null>(null);
	// The body last asked for and its promise, exactly as the population view
	// carries them: one body is one request, and a different body is a new
	// read.
	let wasteKey = "";
	let wasteRequest: Promise<void> | null = null;
	let wasteSeq = 0;

	// A waste class filters only while its zone is in the spec. The toolbar's
	// Clear and an Ask that replaces the spec both take the zone away, and the
	// legend must stop claiming a class is in force.
	watch(
		() =>
			spec.value.temp_zones.some((zone) =>
				zone.name.startsWith(WASTE_ZONE_PREFIX),
			),
		(present) => {
			if (!present) wasteClass.value = null;
		},
	);

	// A bucket selection is exactly the explicit Feature membership; when that
	// leaves the spec (Clear, a restored spec without it) the row stops reading
	// as selected rather than offering a click that would re-add it.
	watch(
		() => spec.value.feature_ids?.length ?? 0,
		(count) => {
			if (count === 0) wasteBucket.value = null;
		},
	);

	// A class filters only while its temporary zone is in the spec. The
	// toolbar's Clear and an Ask that replaces the spec both take the zone
	// away, and the legend must stop claiming a class is in force rather than
	// leave a click that would only re-add it.
	watch(
		() => spec.value.temp_zones.some(isPopulationZone),
		(present) => {
			if (!present) populationClass.value = null;
		},
	);

	// The map control pane's legend counts, from `POST
	// /api/feature-counts`. The Feature points are drawn from the tile
	// map, which carries no whole-set tallies, so the pane reads the four bands
	// and the deactivated and pinned totals from the server. It is the same
	// whole-district count the pane used to take over the loaded Site list, and
	// it moves with the due-soon horizon and the "Show inactive" row.
	const featureCounts = ref<MapReviewFeatureCountsResponse>({
		expired: 0,
		due_soon: 0,
		controlled: 0,
		unknown: 0,
		inactive: 0,
		pinned: 0,
	});
	// Monotonic request id, exactly as the others carry: moving the due-soon
	// slider can overlap two fetches, and a stale tally must be dropped whole.
	let featureCountsSeq = 0;

	// Persist this visit's analysis on every change. Transient state (rows,
	// counts, the map selection, loading/error, the row and checkbox selections)
	// is never written.
	watch(
		[spec, activeTable, page, limit, sort, drawerOpen, drawerExpanded],
		() => {
			saveAnalysis({
				spec: spec.value,
				activeTable: activeTable.value,
				page: page.value,
				limit: limit.value,
				sort: sort.value,
				drawerOpen: drawerOpen.value,
				drawerExpanded: drawerExpanded.value,
			});
		},
		{ deep: true },
	);

	// Computed
	const filtersActive = computed<boolean>(
		() =>
			spec.value.filters.length > 0 ||
			// The zone narrowing is a clause in its own class, and the bulk-write
			// toolbar reads this to decide whether the count it names is filtered:
			// a zone-only spec that read as unfiltered would report a narrowed
			// count as the whole organisation's.
			spec.value.zones.length > 0 ||
			// A temporary zone is a narrowing of the same class — a buffer the
			// operator materialised, drawn on the map and counted here so the
			// selection panel states the filtered count it is about to
			// write.
			spec.value.temp_zones.length > 0 ||
			// An explicit Feature membership is a selection like the rest: the
			// re-treatment bucket's click narrows the ledger, the counts and the
			// map, so a surface that treats this as "unfiltered" would report a
			// narrowed count as the whole organisation's.
			(spec.value.feature_ids?.length ?? 0) > 0 ||
			// The search boxes belong to the surface's own ledgers: a restored
			// visit's box in a table this surface does not read narrows nothing
			// here, so it is not counted as an active filter.
			ledgerTables.value.some((t) => spec.value.search[t].trim() !== ""),
	);
	const activeColumnFilters = computed<MapReviewColumnFilter[]>(() =>
		spec.value.filters.filter((f) => f.table === activeTable.value),
	);

	/**
	 * Every clause the shared spec is actually narrowing by, in the operator's
	 * own words. It is the Analysis pane's applied-filter indicator: the chip
	 * says how many there are and its tooltip lists them, and it must name the
	 * clauses this pane's own question does not read (a species or trap clause)
	 * as well as the ones it does — a report whose numbers are narrowed without
	 * saying by what is worse than no report.
	 *
	 * A filter's label comes from the ledger's registry, so the chip says
	 * "Technician", not "creator"; an observer's id is resolved to the name the
	 * waste payload carries; and a geographic or member-set narrowing that has
	 * no column of its own is stated as what it is.
	 */
	const appliedFilters = computed<AppliedFilterSummary[]>(() => {
		const out: AppliedFilterSummary[] = [];
		for (const clause of spec.value.filters) {
			const column = columnByKey(clause.table, clause.column);
			let detail = "";
			if (clause.op === "enum") {
				detail = (clause.values ?? [])
					.map((value) => {
						if (
							clause.table === "observation" &&
							clause.column === "creator" &&
							typeof value === "number"
						) {
							const creator = waste.value?.creators.find(
								(candidate) => candidate.id === value,
							);
							if (creator) return creator.name;
							return `#${value}`;
						}
						const option = column?.options?.find(
							(candidate) => candidate.value === value,
						);
						return option?.label ?? String(value);
					})
					.join(", ");
			} else if (clause.op === "numberRange") {
				detail = `${clause.min ?? "…"} – ${clause.max ?? "…"}`;
			} else if (clause.op === "dateRange") {
				detail = `${clause.from ?? "…"} → ${clause.to ?? "…"}`;
			} else if (clause.op === "contains") {
				detail = `"${clause.text ?? ""}"`;
			} else if (clause.op === "relation") {
				detail = `${clause.source ?? ""} #${clause.source_id ?? "?"}`;
			}
			out.push({
				key: `filter:${clause.table}:${clause.column}`,
				label: column?.label ?? `${clause.table}.${clause.column}`,
				detail,
			});
		}
		if (spec.value.zones.length > 0) {
			out.push({
				key: "zones",
				label:
					spec.value.zones.length === 1
						? "One standing zone"
						: `${spec.value.zones.length} standing zones`,
				detail: spec.value.zones.join(", "),
			});
		}
		spec.value.temp_zones.forEach((zone, index) => {
			out.push({
				key: `temp-zone:${index}`,
				label: zone.name,
				detail: zone.inside === false ? "outside buffer" : "temporary zone",
			});
		});
		const featureCount = spec.value.feature_ids?.length ?? 0;
		if (featureCount > 0) {
			out.push({
				key: "feature-ids",
				label:
					featureCount === 1
						? "One selected Feature"
						: `${featureCount.toLocaleString()} selected Features`,
				detail: wasteBucket.value
					? `re-treatment bucket ${wasteBucket.value}`
					: "an explicit Feature membership",
			});
		}
		for (const table of MAP_REVIEW_TABLES) {
			const text = spec.value.search[table]?.trim() ?? "";
			if (text !== "") {
				out.push({
					key: `search:${table}`,
					label: `Search in ${table}`,
					detail: `"${text}"`,
				});
			}
		}
		return out;
	});

	/** Whether anything at all narrows the shared spec. The indicator is shown
	 * exactly when this is true, so an unnarrowed report never claims a
	 * filter it does not carry. */
	const specNarrowed = computed(() => appliedFilters.value.length > 0);

	/**
	 * Point the surface at the ledgers it reads, in tab order. Called once on a
	 * surface's own setup: the workbench pins the whole registry, the lab pins the
	 * two ledgers it shows. A surface reads something — an empty set would leave
	 * the grid with no table at all — and the active table is moved to the set's
	 * first when it is no longer one of them, with the reload that keeps the grid
	 * from showing the previous surface's rows.
	 *
	 * The capability documents are re-read for the new set: they are a pure
	 * function of the server's descriptors, but a different set is a different
	 * question, and a table's document is what says how its columns may be
	 * ordered and filtered.
	 */
	function setLedgerTables(tables: MapReviewTable[]): void {
		if (tables.length === 0) return;
		if (
			tables.length === ledgerTables.value.length &&
			tables.every((table, index) => table === ledgerTables.value[index])
		) {
			return;
		}
		ledgerTables.value = [...tables];
		capabilitiesLoad = null;
		if (ledgerTables.value.includes(activeTable.value)) return;
		activeTable.value = ledgerTables.value[0];
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	// Monotonic request id: the view is live-filtered, so responses can land
	// out of order. A response whose id is no longer the latest is dropped
	// whole (rows, counts, map) — never merged with newer state.
	let requestSeq = 0;

	// Text-carrying mutations (search boxes, typing into a filter control) are
	// debounced so a keystroke per POST never reaches a 116k-row resolver;
	// discrete mutations cancel the pending load and fire at once.
	let pendingLoad: number | null = null;

	function flushPendingLoad(): void {
		if (pendingLoad === null) return;
		clearTimeout(pendingLoad);
		pendingLoad = null;
	}

	function scheduleLoad(): void {
		flushPendingLoad();
		pendingLoad = setTimeout(() => {
			pendingLoad = null;
			void load();
		}, FILTER_DEBOUNCE_MS);
	}

	// The ledger's data path (doc/in-progress/query.md §11 Phase 3c/5): the
	// page, the eight counts, the footer and the map highlights are one round of
	// per-resource reads rather than one spec against one resolver. Every table
	// — the Zones surface included — reads the generic path; the resolver's
	// bespoke query endpoint no longer exists (Phase 5).
	async function load(): Promise<void> {
		const seq = ++requestSeq;
		loading.value = true;
		error.value = "";
		loadState.value = "loading";
		timedOutSeconds.value = null;
		try {
			await loadLedger(seq);
			// A round superseded while it ran owns nothing: the newer round's
			// state is the one the surface reads.
			if (seq === requestSeq) loadState.value = "resolved";
		} catch (err) {
			if (seq !== requestSeq) return;
			error.value = apiErrorMessage(err, "Failed to load table rows");
			errorTitle.value = "Table query failed";
			loadState.value = "failed";
			const deadline = isRequestTimeout(err) ? err.config?.timeout : undefined;
			timedOutSeconds.value =
				typeof deadline === "number" && deadline > 0 ? deadline / 1000 : null;
		} finally {
			if (seq === requestSeq) loading.value = false;
		}
	}

	/**
	 * The load through the generic endpoints, all in flight together:
	 *
	 *   1. `QUERY /api/<active resource>` — the page, its `filtered_total` and the
	 *      minted identity set the active table's map layer highlights;
	 *   2-9. `QUERY /api/<each other ledger resource>` — the count strip. The
	 *      generic layer narrows one resource at a time (plan §8.3/§13.6), so the
	 *      counts are one read each, and each also mints the highlight of the layer
	 *      it counts. The resources read are the surface's own ledger set, not the
	 *      whole registry: a page that shows two ledgers asks two questions;
	 *   10. `POST /api/summary` — the footer over the active table's columns, for
	 *      the tables the grid registry declares; and
	 *   11. `QUERY /api/treatment` with the over verdict — the over-rate set, where
	 *      the surface reads the treatment ledger.
	 *
	 * Every mint is the narrowing's own id set, read by the same scan that counts
	 * it, so a table's count and its highlight cannot disagree.
	 *
	 * The round is all-or-nothing: a single refusal (a clause the resource does
	 * not accept, a summary for a resource whose columns it does not aggregate)
	 * leaves the whole grid as it was under one error toast rather than repainting
	 * half of it.
	 */
	async function loadLedger(seq: number): Promise<void> {
		await loadCapabilities();
		const table = activeTable.value;
		const ledgers = ledgerTables.value;
		const activeIndex = ledgers.indexOf(table);
		const live = spec.value;
		const declared = capabilities.value;
		const location = ledgerLocation(live);
		// A filter set on one ledger reaches the records related to it: the tables
		// carrying a clause of their own are read first (round one), each minting
		// its identity set, and every other table's read then states a relation
		// clause to each of those sets (round two). Two rounds rather than one is
		// the cost of not growing the query grammar — the single-record relation
		// form cannot name a set — and it is what makes the Features and Sites the
		// kept Treatments touch narrow with the Treatments (the legacy resolver's
		// cross-table keep graph, D4's deliberate omission, re-created through the
		// identity sets every ledger read already mints). Only the surface's own
		// ledgers are read, so the rounds run over `ledgers`, not the registry's
		// whole set.
		const filtered = ledgers.filter(
			(t) =>
				live.filters.some((clause) => clause.table === t) ||
				// An explicit Feature membership is a Feature clause for this
				// round: its read mints the identity set every other table's
				// relation clause narrows through, exactly as a column clause's
				// read does.
				(t === "feature" && (live.feature_ids?.length ?? 0) > 0),
		);
		const sets: { table: MapReviewTable; token: string }[] = [];
		await Promise.all(
			filtered.map(async (t) => {
				const env = await apiClient.JSONQuery<
					MapReviewQueryEnvelope<MapReviewRow>
				>(LEDGER_QUERY_PATH[t], {
					limit: 1,
					filter: ledgerFilter(t, live, declared),
					location: t === "zone" ? undefined : location,
					tz_offset_minutes: ledgerTZOffset(t, live),
					identity_set: { mint: true },
				} satisfies MapReviewQueryBody);
				const token = env.metadata.identity_set?.id;
				if (token) sets.push({ table: t, token });
			}),
		);
		const related: Record<MapReviewTable, MapReviewQueryBodyFilter[]> =
			{} as Record<MapReviewTable, MapReviewQueryBodyFilter[]>;
		for (const t of ledgers) related[t] = relatedSetClauses(t, sets, declared);
		const reads = ledgers.map((t) =>
			apiClient.JSONQuery<MapReviewQueryEnvelope<MapReviewRow>>(
				LEDGER_QUERY_PATH[t],
				{
					// The active table asks for the page in hand; every other table asks
					// for one row — the smallest page the gate admits — and only its
					// count and mint are read from the answer.
					...(t === table
						? {
								offset: page.value * limit.value,
								limit: limit.value,
								order: ledgerOrder(activeSort.value),
							}
						: { limit: 1 }),
					filter: [...ledgerFilter(t, live, declared), ...related[t]],
					// The Zones surface is neither drawn nor narrowed by the operator's
					// geography: it lists the district's units so that a selection can be
					// made and unmade (query.go `keepZone`), so its own read states no
					// location.
					location: t === "zone" ? undefined : location,
					tz_offset_minutes: ledgerTZOffset(t, live),
					identity_set: { mint: true },
				} satisfies MapReviewQueryBody,
			),
		);
		// The footer aggregate is the grid registry's columns over the whole
		// filtered set, and `POST /api/summary` serves the tables that registry
		// declares. A ledger outside it — the lab's pools, and the Zones grid,
		// whose descriptor is hand-built and never paired with a registry table —
		// has no footer row: asking for one is refused, and there is no column of
		// the grid's to aggregate, so the footer is left empty instead. The zone
		// case is not theoretical: the refusal is a 422 that rejects the whole
		// load round, so the Zones tab would keep the previous table's rows.
		const asksSummary = MAP_REVIEW_TABLES.includes(table) && table !== "zone";
		// The over-rate set is the treatment map's own highlight, and a surface
		// that does not carry the treatment ledger carries no treatment layer to
		// draw it on, so its read is not made.
		const readsOverRate = ledgers.includes("treatment");
		const [pages, footer, over] = await Promise.all([
			Promise.all(reads),
			asksSummary
				? apiClient.JSONPost<MapReviewSummaryResponse>("/api/summary", {
						resource: table,
						filter: [...ledgerFilter(table, live, declared), ...related[table]],
						location,
						tz_offset_minutes: ledgerTZOffset(table, live),
						// No page, no order and no mint: a footer is over the whole
						// filtered set, and the resource refuses those fields (422)
						// rather than ignoring them.
					} satisfies MapReviewSummaryRequest)
				: Promise.resolve<MapReviewSummaryResponse | null>(null),
			readsOverRate
				? apiClient.JSONQuery<MapReviewQueryEnvelope<MapReviewRow>>(
						LEDGER_QUERY_PATH.treatment,
						{
							limit: 1,
							filter: [
								...ledgerFilter("treatment", live, declared),
								...related.treatment,
								OVER_RATE_CLAUSE,
							],
							location,
							tz_offset_minutes: ledgerTZOffset("treatment", live),
							identity_set: { mint: true },
						} satisfies MapReviewQueryBody,
					)
				: Promise.resolve<MapReviewQueryEnvelope<MapReviewRow> | null>(null),
		]);
		if (seq !== requestSeq) return;
		const active = pages[activeIndex];
		rows.value = active.items;
		total.value = active.metadata.filtered_total;
		// Each table's count and selection are read from the same answer, so the
		// count strip and the map highlights are one round rather than two.
		const nextCounts = { ...counts.value };
		const nextMap = { ...mapSelection.value };
		ledgers.forEach((t, index) => {
			nextCounts[t] = pages[index].metadata.filtered_total;
			nextMap[t] = ledgerMapSelection(t, pages[index].metadata.identity_set);
		});
		counts.value = nextCounts;
		mapSelection.value = nextMap;
		summary.value = footer?.columns ?? {};
		// An empty over-rate set fits every cap and ships `[]`; a set above the cap
		// ships no ids, which `exact` records — the same distinction the legacy
		// answer drew at its own cap. A surface that made no over-rate read carries
		// no set at all, which is the empty exact one.
		const overRate = over?.metadata.identity_set?.ids ?? null;
		overRateIDs.value = overRate ?? [];
		overRateExact.value = over === null || overRate !== null;
	}

	// One OPTIONS round per session, memoised as the in-flight promise so two
	// loads racing at mount share it. The documents are a pure function of the
	// server's descriptors (D6/D7), so no filter change can move them and a
	// refetch on every keystroke would be ten requests for an answer that cannot
	// have changed. One per ledger SET, though: each document is only read for a
	// table the surface actually reads (see setLedgerTables, which drops the
	// memo when the set changes).
	let capabilitiesLoad: Promise<void> | null = null;

	function loadCapabilities(): Promise<void> {
		capabilitiesLoad ??= Promise.all(
			ledgerTables.value.map(async (table) => {
				try {
					capabilities.value[table] =
						await apiClient.JSONOptions<MapReviewCapability>(
							LEDGER_QUERY_PATH[table],
						);
				} catch {
					// A resource that does not answer OPTIONS keeps the presentation
					// registry's declaration of its columns (capability.ts), so a
					// missing document degrades the ledger to what it shipped rather
					// than failing the load.
				}
			}),
		).then(() => undefined);
		return capabilitiesLoad;
	}

	/** The ledger's columns for one table: the presentation registry with the
	 * capability the server declares folded onto it (capability.ts). What a column
	 * can do — whether it may be ordered, which filter controls it offers — is
	 * read from here rather than from the registry alone. */
	function columnsFor(table: MapReviewTable): MapReviewColumn[] {
		return mergedColumns(table, capabilities.value[table]);
	}

	// Fetch one row by id, wherever it falls. The ledger only carries the
	// active table's current page, but the map can select any record — a Trap
	// Station or off-station Trap Instance dot is often off that page — so the
	// popup reads the row back through the same generic QUERY every other read
	// uses, narrowed to the one row by the resource's declared identity property
	// (`queryspec.IdentityProperty`, stated with `equal` alone). It is the only
	// narrowing, so the counts and map selections the page reports are
	// unaffected. The zone and temporary-zone narrowings are left out for the
	// same reason: the popup must be able to name a record the operator's
	// current geographic selection does not cover, and the row's own id is the
	// whole request.
	async function fetchRow(
		table: MapReviewTable,
		id: number,
	): Promise<MapReviewRow | null> {
		const response = await apiClient.JSONQuery<
			MapReviewQueryEnvelope<MapReviewRow>
		>(LEDGER_QUERY_PATH[table], {
			limit: 1,
			filter: [{ property: "id", operator: "equal", value: id }],
		} satisfies MapReviewQueryBody);
		return response.items[0] ?? null;
	}

	// The station's Trap Instances, for the station popup's counts-over-time
	// chart. The request is seeded from the LIVE spec so the chart narrows with
	// everything else — the district's standing zones and the
	// session's buffers included (`ledgerLocation`) — and then carries the same
	// station relation the popup's Instances link follows: the instances whose
	// station is this station.
	//
	// What it no longer does is the resolver's cross-table propagation: a
	// species, sex or count clause set on another table used to narrow the
	// instances this series was drawn from through the keep graph, and the
	// generic layer narrows one resource at a time (D4, plan §8.3/§13.6), so the
	// chart now follows the trap-instance table's own clauses alone. That
	// widening is the regression Phase 5 accepted when the ledger moved onto the
	// per-resource QUERY.
	//
	// The chart reads the station's whole series rather than the ledger's page,
	// so it asks for the server's page ceiling in one request and reads each
	// instance's own counts matrix off the row.
	async function fetchStationInstances(
		stationID: number,
	): Promise<MapReviewTrapInstanceRow[]> {
		await loadCapabilities();
		const live = spec.value;
		// The operator's own trap-instance clauses, minus any relation clause
		// they set by following this same link: the station's clause below
		// replaces it, so it is never duplicated.
		const own = ledgerFilter(
			"trap_instance",
			{
				...live,
				filters: live.filters.filter(
					(f) => !(f.table === "trap_instance" && f.column === RELATION_COLUMN),
				),
			},
			capabilities.value,
		);
		const response = await apiClient.JSONQuery<
			MapReviewQueryEnvelope<MapReviewRow>
		>(LEDGER_QUERY_PATH.trap_instance, {
			limit: 1000,
			// The operator's zone narrowing is carried, not the station's:
			// the chart is the station's series as the ledger currently
			// reads it, and the relation clause is the only clause this
			// request adds.
			location: ledgerLocation(live),
			filter: [
				...own,
				{
					property: RELATION_COLUMN,
					operator: "relation",
					value: { source: "trap_station", source_id: stationID },
				},
			],
			tz_offset_minutes: ledgerTZOffset("trap_instance", live),
		} satisfies MapReviewQueryBody);
		return response.items as MapReviewTrapInstanceRow[];
	}

	function setActiveTable(t: MapReviewTable): void {
		// The grid can only show a table its surface reads: an off-set table has
		// no page in hand and no count in the strip, so a switch to it would take
		// the grid away from the ledgers this page is showing.
		if (!ledgerTables.value.includes(t)) return;
		if (activeTable.value === t) return;
		activeTable.value = t;
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	function setSearch(t: MapReviewTable, text: string): void {
		if (spec.value.search[t] === text) return;
		spec.value.search[t] = text;
		// Narrowing resets paging: a page number that was valid for the wider
		// result set can be past the end of the narrower one.
		page.value = 0;
		scheduleLoad();
	}

	function setFilter(
		f: MapReviewColumnFilter | null,
		column: string,
		table: MapReviewTable,
	): void {
		const rest = spec.value.filters.filter(
			(existing) => !(existing.table === table && existing.column === column),
		);
		spec.value.filters = f ? [...rest, f] : rest;
		page.value = 0;
		if (
			f &&
			(f.op === "contains" || f.op === "numberRange" || f.op === "dateRange")
		) {
			scheduleLoad();
			return;
		}
		flushPendingLoad();
		void load();
	}

	function clearFilters(t?: MapReviewTable): void {
		spec.value.filters = t
			? spec.value.filters.filter((f) => f.table !== t)
			: [];
		// An explicit Feature membership is a selection like any other clause,
		// so "clear all" takes it with the rest rather than leaving the ledger
		// narrowed to a bucket the operator can no longer see or unclick.
		if (!t) {
			spec.value.feature_ids = [];
			wasteBucket.value = null;
			// The standing zones and the session's buffers are narrowings of the
			// same spec, and the Filters pane lists them beside the column
			// clauses. "Clear all" must therefore take them too, or the pane
			// would still show applied rows after the operator emptied it. A
			// table-scoped clear is a different question and leaves them alone.
			spec.value.zones = [];
			spec.value.temp_zones = [];
			spec.value.buffer_combine = "union";
		}
		// The button reads "Clear all filters and search", and a search that
		// narrowed the counts would leave the clear looking half done. Every box
		// is emptied, the lab's Pools box included: it is one of the ledgers the
		// lab's surface reads, and a spec restored from a visit there carries it.
		if (t) spec.value.search[t] = "";
		else
			spec.value.search = {
				site: "",
				feature: "",
				treatment: "",
				observation: "",
				trap_station: "",
				trap_instance: "",
				species_count: "",
				trap_sample: "",
				zone: "",
				pool: "",
			};
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	/**
	 * Remove exactly the applied item whose stable key was clicked, in the key
	 * grammar `appliedFilters` publishes: `filter:<table>:<column>`, `zones`,
	 * `temp-zone:<index>`, `feature-ids` or `search:<table>`.
	 *
	 * It sits beside `clearFilters` because the Filters pane offers both, and
	 * they answer different questions. "Clear all" is the whole-spec reset the
	 * hero button has always been; a row's remove is one narrowing out of
	 * several, and must leave the rest in force — reusing `clearFilters` there
	 * would wipe clauses the operator never pointed at and hand back a ledger
	 * wider than the one they were reading.
	 *
	 * Each key names the field `appliedFilters` read that row from, which is the
	 * only thing that makes the row addressable: one clause is one entry of
	 * `spec.filters`; the standing-zone row is the whole `zones` list (the list
	 * is what `setZones` writes, and a list of ids would need a key the pane
	 * cannot build); a buffer row is one element of `temp_zones` and nothing
	 * else; the explicit Feature membership is `feature_ids`, with
	 * `wasteBucket` cleared beside it because the bucket is a name for a
	 * selection that no longer exists and a later row would otherwise describe
	 * the wrong Features; and a legacy search row is that one table's entry in
	 * `spec.search`.
	 *
	 * An unrecognised key — an older row, a click that raced a reload — is a
	 * no-op and reloads nothing: a round for a spec that did not change is a
	 * scan over 116k treatments spent on answering the question already on
	 * screen.
	 */
	function clearApplied(key: string): void {
		if (key === "zones") {
			if (spec.value.zones.length === 0) return;
			spec.value.zones = [];
		} else if (key === "feature-ids") {
			if ((spec.value.feature_ids?.length ?? 0) === 0) return;
			spec.value.feature_ids = [];
			wasteBucket.value = null;
		} else if (key.startsWith("temp-zone:")) {
			const index = Number.parseInt(key.slice("temp-zone:".length), 10);
			if (
				!Number.isInteger(index) ||
				index < 0 ||
				index >= spec.value.temp_zones.length
			) {
				return;
			}
			// By index, and the rest of the list keeps its order: the buffers
			// compose in the order they were added, and the keys of the rows
			// below the removed one are re-read from the same list.
			const buffers = [...spec.value.temp_zones];
			buffers.splice(index, 1);
			spec.value.temp_zones = buffers;
		} else if (key.startsWith("search:")) {
			const table = key.slice("search:".length) as MapReviewTable;
			// Only a table `appliedFilters` lists carries a search row. A key
			// naming anything else names nothing — and `in` alone would answer
			// yes for Object.prototype's own keys.
			if (!MAP_REVIEW_TABLES.includes(table)) return;
			if (spec.value.search[table] === "") return;
			spec.value.search[table] = "";
		} else if (key.startsWith("filter:")) {
			const rest = key.slice("filter:".length);
			const sep = rest.indexOf(":");
			if (sep <= 0 || sep === rest.length - 1) return;
			const table = rest.slice(0, sep);
			const column = rest.slice(sep + 1);
			const kept = spec.value.filters.filter(
				(clause) => !(clause.table === table && clause.column === column),
			);
			// The key may name a clause another surface cleared first; there is
			// then nothing to remove and nothing to reload.
			if (kept.length === spec.value.filters.length) return;
			spec.value.filters = kept;
		} else {
			return;
		}
		// Removing a narrowing widens the result set, so paging resets for the
		// reason `setFilter` resets it: a page number valid for the narrower set
		// can be past the end of the wider one.
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	// Follow a relation-link cell: narrow the target table to exactly the
	// records related to one record of the link's source, and switch to that
	// table. The clause is an ordinary spec filter, so it composes with the
	// target's existing filters, shows in the filters-active state, is
	// clearable like any other, and propagates through the graph. At most one
	// relation filter per table: following a second link to the same table
	// replaces the first.
	function followRelation(filter: MapReviewColumnFilter): void {
		if (filter.op !== "relation" || !filter.source) return;
		// The link's target must be a ledger this surface reads. A relation cell
		// the surface does not carry a ledger for (a pool row's Site link on the
		// lab page) has nowhere to be shown, and switching the grid to it would
		// index a page the surface never read.
		if (!ledgerTables.value.includes(filter.table)) return;
		const rest = spec.value.filters.filter(
			(existing) =>
				!(existing.table === filter.table && existing.column === filter.column),
		);
		spec.value.filters = [...rest, filter];
		activeTable.value = filter.table;
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	// The drawing mode the next armed draw will use. It is deliberately not a
	// spec change: the mode says how the operator draws a zone, whether a
	// freehand ring, an approximated circle or a clicked vertex list. Nothing
	// reloads.
	function setDrawMode(m: DrawMode): void {
		drawMode.value = m;
	}

	// The zone narrowing, set by the zone filter control and by the Zones
	// grid's Records cell, which follows one zone as its own selection. It is a
	// geographic narrowing of every spatial table, so it is one list on the spec
	// rather than a per-table clause: selecting several
	// narrows to their union. Debounced like the other multi-select controls,
	// so ticking several zones in a row is one query.
	function setZones(ids: number[]): void {
		spec.value.zones = ids;
		page.value = 0;
		scheduleLoad();
	}

	// The session's temporary zones, written by the buffer form, by Ask's
	// spatial clause and by the temp-zone overlay's own Clear. A buffer is a
	// geographic narrowing in the same class as a standing zone — the server
	// unions the two into one containment test — so all three act exactly as
	// setZones does: page 0, then a debounced load, so the grid, the counts and
	// the map narrow together rather than the map alone.
	function setTempZones(zones: MapReviewTempZone[]): void {
		spec.value.temp_zones = zones;
		page.value = 0;
		scheduleLoad();
	}

	// One buffer added, as the buffer form and Ask do; the returned label
	// travels with it so the filter summary can name what was buffered.
	function addTempZone(zone: MapReviewTempZone): void {
		spec.value.temp_zones = [...spec.value.temp_zones, zone];
		page.value = 0;
		scheduleLoad();
	}

	// Every buffer dropped at once. A temporary zone is session-scoped and
	// unsaved, so clearing is the whole of its life: nothing else refers to it,
	// and the map stops drawing it the moment it leaves the spec.
	function clearTempZones(): void {
		spec.value.temp_zones = [];
		page.value = 0;
		scheduleLoad();
	}

	// One buffer's side: true keeps the rows it contains, false keeps the rows it
	// does not. The side is a property of the shape the operator already holds,
	// so flipping it re-narrows the same set through the spec and never
	// re-materialises the buffer.
	function setTempZoneInside(index: number, inside: boolean): void {
		const zone = spec.value.temp_zones[index];
		if (!zone || (zone.inside ?? true) === inside) return;
		spec.value.temp_zones = spec.value.temp_zones.map((candidate, i) =>
			i === index ? { ...candidate, inside } : candidate,
		);
		page.value = 0;
		scheduleLoad();
	}

	// How several inside buffers compose. Standing zones are unaffected: they
	// always compose by union.
	function setBufferCombine(mode: MapReviewBufferCombine): void {
		if ((spec.value.buffer_combine ?? "union") === mode) return;
		spec.value.buffer_combine = mode;
		page.value = 0;
		scheduleLoad();
	}

	function setSort(s: MapReviewSort | null): void {
		sort.value = s;
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	function setPage(p: number): void {
		page.value = p;
		flushPendingLoad();
		void load();
	}

	// Selection never moves the map and never re-queries: it is a highlight.
	// Every table's rows are selectable, observations included — an observation
	// row selects the record, which the popup reads back from the page, else by
	// id when the page does not carry it (a link can name any record).
	function select(table: MapReviewTable, id: number): void {
		selection.value = { table, id };
	}

	function clearSelection(): void {
		selection.value = null;
	}

	function openDrawer(table?: MapReviewTable): void {
		drawerOpen.value = true;
		// The drawer opens on the grid it holds: a table this surface does not read
		// has no ledger here to open, so the drawer comes up on the one already
		// showing rather than on a table with no page.
		if (!table || !ledgerTables.value.includes(table)) return;
		page.value = 0;
		activeTable.value = table;
		flushPendingLoad();
		void load();
	}

	function closeDrawer(): void {
		drawerOpen.value = false;
	}

	// The drag writes here, and the store clamps: the same number bounds the
	// drawer's height and the rails' bottom edge, so neither caller can push the
	// drawer past the map it covers.
	function setDrawerFraction(fraction: number): void {
		drawerFraction.value = Math.min(
			MAX_DRAWER_FRACTION,
			Math.max(MIN_DRAWER_FRACTION, fraction),
		);
	}

	function setDrawerExpanded(on: boolean): void {
		setDrawerFraction(on ? MAX_DRAWER_FRACTION : DEFAULT_DRAWER_FRACTION);
	}

	function toggleFiltersOpen(): void {
		filtersOpen.value = !filtersOpen.value;
	}

	/**
	 * Adopt a whole view state in one assignment: the URL a visit was opened
	 * with. The per-field setters are the wrong tool here — each resets the page
	 * and fires its own round — so the fields are assigned directly and any
	 * pending debounced round (which belongs to the pre-hydration state) is
	 * cancelled; the caller issues the page's one round afterwards.
	 */
	function hydrate(state: MapReviewHydratedState): void {
		spec.value = state.spec;
		activeTable.value = state.activeTable;
		page.value = state.page;
		limit.value = state.limit;
		sort.value = state.sort;
		drawerOpen.value = state.drawerOpen;
		flushPendingLoad();
	}

	// The reporting dashboard's one aggregate request. The whole spec travels —
	// the server resolves it with the same resolver the ledger and the map use,
	// so no panel can disagree with the table under it — and so does the viewer's
	// zone offset, because the server buckets every month series (and the
	// usage-vs-catches alignment) in the viewer's months, not UTC's.
	async function loadDashboard(): Promise<void> {
		const seq = ++dashboardSeq;
		dashboardLoading.value = true;
		error.value = "";
		const request: MapReviewDashboardRequest = {
			spec: spec.value,
			tz_offset_minutes: clientZoneOffsetMinutes(),
		};
		try {
			const response = await apiClient.JSONPost<MapReviewDashboardResponse>(
				"/api/dashboard",
				request,
			);
			if (seq !== dashboardSeq) return;
			dashboard.value = response;
		} catch (err) {
			if (seq !== dashboardSeq) return;
			error.value = apiErrorMessage(err, "Failed to load the dashboard");
			errorTitle.value = "Dashboard";
		} finally {
			if (seq === dashboardSeq) dashboardLoading.value = false;
		}
	}

	// The surveillance section's one request. It carries no body: the server
	// defaults the report year to the current one. The denominators are analysed
	// trap samples; the baselines are the five prior seasons (the metric
	// definitions live in doc/design/mvp/dashboard.md).
	async function loadSurveillance(): Promise<void> {
		const seq = ++surveillanceSeq;
		surveillanceLoading.value = true;
		error.value = "";
		try {
			const response = await apiClient.JSONPost<MapReviewSurveillanceResponse>(
				"/api/surveillance",
				{},
			);
			if (seq !== surveillanceSeq) return;
			surveillance.value = response;
		} catch (err) {
			if (seq !== surveillanceSeq) return;
			error.value = apiErrorMessage(err, "Failed to load trap surveillance");
			errorTitle.value = "Surveillance";
		} finally {
			if (seq === surveillanceSeq) surveillanceLoading.value = false;
		}
	}

	/**
	 * The population view's one read. The whole spec travels — the server
	 * resolves the estimand with the same rules the ledger narrows by, so the
	 * cells, the stations, the class breaks and the season charts cannot
	 * disagree with the table under them — and so does the viewer's zone
	 * offset, because the week axis is the viewer's weeks and not UTC's.
	 *
	 * While a bivariate class is in force the breaks it was cut with travel
	 * too: the class was one of nine cut from the FULL field, and the server
	 * re-cutting the narrowed subset's terciles would re-third it — the same
	 * colours would then mean different levels, and the legend would be
	 * describing a different picture. With the field omitted, a class-free read
	 * lets the server compute the breaks the field in scope earns.
	 *
	 * One body is one request: the pane's mount and its spec watch can both ask
	 * for the same view, and the second ask joins the first. The promise is
	 * returned so a caller can await the read it started.
	 */
	function loadPopulation(): Promise<void> {
		const request = {
			spec: spec.value,
			tz_offset_minutes: clientZoneOffsetMinutes(),
			...(populationClass.value !== null && population.value
				? { breaks: population.value.breaks }
				: {}),
		} satisfies MapReviewPopulationRequest;
		const key = JSON.stringify(request);
		if (populationRequest && key === populationKey) return populationRequest;
		const seq = ++populationSeq;
		populationKey = key;
		populationLoading.value = true;
		error.value = "";
		populationRequest = (async () => {
			try {
				const response = await apiClient.JSONPost<MapReviewPopulationView>(
					"/api/map-review/population",
					request,
				);
				if (seq !== populationSeq) return;
				population.value = response;
			} catch (err) {
				if (seq !== populationSeq) return;
				// A refused read is retried when the spec asks again: holding the
				// key would make the same body a silent no-op for the session.
				if (populationKey === key) populationKey = "";
				error.value = apiErrorMessage(
					err,
					"Failed to load the population view",
				);
				errorTitle.value = "Population";
			} finally {
				if (seq === populationSeq) populationLoading.value = false;
			}
		})();
		return populationRequest;
	}

	/**
	 * Send the bivariate cells to the workbench map, or take them off it. It is
	 * the layer's whole control: the map draws the payload's cells while
	 * `populationOnMap` is true, so nothing is fetched, clipped or rebuilt here.
	 * A fresh send starts visible; the Layers pane's checkbox only hides the
	 * fill, and the payload stays put behind it.
	 */
	function sendPopulationToMap(on: boolean): void {
		populationOnMap.value = on;
		if (on) populationVisible.value = true;
	}

	/** Show or hide the sent population fill without taking the layer off the
	 * map: the payload stays put, so bringing the fill back reads nothing. */
	function togglePopulationVisible(): void {
		populationVisible.value = !populationVisible.value;
	}

	/**
	 * A clicked bivariate class. The 3×3 grid doubles as the legend and the
	 * filter: clicking a class materialises that class's dissolved geometry —
	 * the server's union, never a client-side dissolve — as the session's
	 * `Population: <class name>` temporary zone, so the ledger, the counts and
	 * the map narrow by it exactly as they do for a buffer. Clicking the class
	 * in force removes the zone and clears the class.
	 *
	 * Only the population view's own zone is replaced; every other temporary
	 * zone — an operator's buffer, usually — is preserved. And only one is ever
	 * present: two class zones would narrow to the union of two disjoint cell
	 * sets, which no question on the grid asks.
	 *
	 * The ledger read is fired here (page 0, like every other geographic
	 * narrowing). The population view is not: the Analysis pane is its one
	 * caller, and its spec watch re-reads under the new spec after this returns
	 * — by then `populationClass` names the class, so the read carries the
	 * breaks it was classed with (see loadPopulation) and the legend keeps
	 * describing the geometry the operator clicked.
	 */
	function togglePopulationClass(index: number): void {
		if (populationClass.value === index) {
			spec.value.temp_zones = spec.value.temp_zones.filter(
				(zone) => !isPopulationZone(zone),
			);
			populationClass.value = null;
		} else {
			const cls = population.value?.classes.find(
				(candidate) => candidate.index === index,
			);
			// Before the first payload there is no class geometry to materialise,
			// so the click does nothing rather than inventing a shape. The same
			// holds for a class with no cells in scope: its MultiPolygon is empty,
			// and the spec would (rightly) refuse it.
			if (!cls) return;
			const geometry = cls.geometry as GeoJSON.MultiPolygon;
			if (!geometry?.coordinates?.length) return;
			spec.value.temp_zones = [
				...spec.value.temp_zones.filter((zone) => !isPopulationZone(zone)),
				{
					name: `${POPULATION_ZONE_PREFIX}${cls.name}`,
					// The server dissolved the class; the wire type carries the
					// geometry opaquely (the endpoint's own shape is not this
					// client's), so it is taken as the MultiPolygon the spec's
					// temporary zone declares.
					geometry,
				},
			];
			populationClass.value = index;
		}
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	/**
	 * The waste view's one read. The whole spec travels — the server resolves
	 * the estimand with the same rules the ledger narrows by, so the totals, the
	 * Sites, the buckets, the observers and the H3 cells cannot disagree with
	 * the table under them — and so does the viewer's zone offset, because a
	 * visit is one Site on one local day.
	 *
	 * While a bivariate class is in force the breaks it was cut with travel too:
	 * the class was one of nine cut from the full cell set, and re-cutting the
	 * narrowed subset's terciles would make the same colours mean different
	 * levels, exactly as in the population view. One body is one request.
	 */
	function loadWaste(): Promise<void> {
		const request = {
			spec: spec.value,
			tz_offset_minutes: clientZoneOffsetMinutes(),
			...(wasteClass.value !== null && waste.value
				? { breaks: waste.value.breaks }
				: {}),
		} satisfies MapReviewWasteRequest;
		const key = JSON.stringify(request);
		if (wasteRequest && key === wasteKey) return wasteRequest;
		const seq = ++wasteSeq;
		wasteKey = key;
		wasteLoading.value = true;
		error.value = "";
		wasteRequest = (async () => {
			try {
				const response = await apiClient.JSONPost<MapReviewWasteView>(
					"/api/map-review/waste",
					request,
				);
				if (seq !== wasteSeq) return;
				waste.value = response;
			} catch (err) {
				if (seq !== wasteSeq) return;
				// A refused read is retried when the spec asks again: holding the
				// key would make the same body a silent no-op for the session.
				if (wasteKey === key) wasteKey = "";
				error.value = apiErrorMessage(err, "Failed to load the waste view");
				errorTitle.value = "Waste";
			} finally {
				if (seq === wasteSeq) wasteLoading.value = false;
			}
		})();
		return wasteRequest;
	}

	/**
	 * Send the waste cells to the workbench map, or take them off it. The map
	 * draws the payload's cells while `wasteOnMap` is true and drops them while
	 * a new read is in flight, so a filter change never leaves the previous
	 * filter's fill standing. A fresh send starts visible; the Layers pane's
	 * checkbox only hides the fill, and the payload stays put behind it.
	 */
	function sendWasteToMap(on: boolean): void {
		wasteOnMap.value = on;
		if (on) wasteVisible.value = true;
	}

	/** Show or hide the sent waste fill without taking the layer off the map:
	 * the payload stays put, so bringing the fill back reads nothing. */
	function toggleWasteVisible(): void {
		wasteVisible.value = !wasteVisible.value;
	}

	/**
	 * A clicked bivariate class. The 3×3 grid doubles as the legend and the
	 * filter: clicking a class materialises that class's dissolved geometry —
	 * the server's union, never a client-side dissolve — as the session's
	 * `Waste: <class name>` temporary zone, so the ledger, the counts and the
	 * map narrow by it exactly as they do for a buffer. Clicking the class in
	 * force removes the zone and clears the class. Only the waste view's own
	 * zone is replaced; every other temporary zone (an operator's buffer, the
	 * population view's class) is preserved.
	 */
	function toggleWasteClass(index: number): void {
		const dropWasteZones = (zone: MapReviewTempZone) =>
			!zone.name.startsWith(WASTE_ZONE_PREFIX);
		if (wasteClass.value === index) {
			spec.value.temp_zones = spec.value.temp_zones.filter(dropWasteZones);
			wasteClass.value = null;
		} else {
			const cls = waste.value?.classes.find(
				(candidate) => candidate.index === index,
			);
			// Before the first payload there is no class geometry to materialise,
			// and a class with no cells in scope has an empty MultiPolygon the
			// spec would rightly refuse.
			if (!cls) return;
			const geometry = cls.geometry as GeoJSON.MultiPolygon;
			if (!geometry?.coordinates?.length) return;
			spec.value.temp_zones = [
				...spec.value.temp_zones.filter(dropWasteZones),
				{ name: `${WASTE_ZONE_PREFIX}${cls.name}`, geometry },
			];
			wasteClass.value = index;
		}
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	/**
	 * Select a re-treatment bucket, or clear the one in force. The selection is
	 * `spec.feature_ids` — the bucket's own membership — so the Feature ledger
	 * narrows by identity, the related records narrow through the identity set
	 * the Feature read mints, and the waste endpoint, the counts and the map all
	 * read the same set. Clicking the selected bucket clears it.
	 *
	 * While a bucket is in force the other buckets are inert: the waste payload
	 * is computed under the selection, so their members are no longer in it.
	 * That is the same rule a bivariate class follows.
	 */
	function selectWasteBucket(bucket: MapReviewWasteBucket): void {
		if (wasteBucket.value === bucket.key) {
			spec.value.feature_ids = [];
			wasteBucket.value = null;
		} else {
			if (bucket.featureIds.length === 0) return;
			spec.value.feature_ids = [...bucket.featureIds];
			wasteBucket.value = bucket.key;
		}
		page.value = 0;
		flushPendingLoad();
		void load();
	}

	/**
	 * Narrow the ledger and the map to one observer, or clear that narrowing.
	 * The clause is the observation ledger's own Technician filter — the visit's
	 * creator id, never the joined display name — so it composes with every
	 * other clause and is cleared like one. Clicking the observer in force
	 * clears it.
	 */
	function filterByObserver(id: number | null): void {
		if (id === null) {
			setFilter(null, "creator", "observation");
			return;
		}
		const active = spec.value.filters.find(
			(clause) =>
				clause.table === "observation" &&
				clause.column === "creator" &&
				clause.op === "enum" &&
				(clause.values ?? []).length === 1 &&
				Number(clause.values?.[0]) === id,
		);
		if (active) {
			setFilter(null, "creator", "observation");
			return;
		}
		setFilter(
			{ table: "observation", column: "creator", op: "enum", values: [id] },
			"creator",
			"observation",
		);
	}

	// The map control pane's one count request over the district's whole Feature
	// set: the four control bands against the due-soon horizon, plus the
	// deactivated and pinned totals. It is deliberately not part of `load` — it
	// counts the whole district rather than the filtered page, and it moves with
	// the horizon slider and the "Show inactive" row rather than with the spec.
	async function loadFeatureCounts(
		horizonDays: number,
		includeInactive: boolean,
	): Promise<void> {
		const seq = ++featureCountsSeq;
		const request: MapReviewFeatureCountsRequest = {
			horizon_days: horizonDays,
			include_inactive: includeInactive,
		};
		try {
			const response = await apiClient.JSONPost<MapReviewFeatureCountsResponse>(
				"/api/feature-counts",
				request,
			);
			if (seq !== featureCountsSeq) return;
			featureCounts.value = response;
		} catch (err) {
			if (seq !== featureCountsSeq) return;
			error.value = apiErrorMessage(err, "Failed to load map legend counts");
			errorTitle.value = "Map legend";
		}
	}

	// A clicked chart mark. The bucket's key becomes an ordinary spec clause —
	// the same clause the ledger's own filter row would set, so the drawer and
	// the map both narrow with it — and then the dashboard
	// is refetched, so the panels show the narrowed spec rather than the one they
	// were rendered from.
	async function drillDown(filter: MapReviewColumnFilter): Promise<void> {
		setFilter(filter, filter.column, filter.table);
		await loadDashboard();
	}

	// Set (an RFC3339 instant) or clear (null) the planned revisit date of the
	// selected Features, in ONE request. The spec says which Features the table
	// is showing; `selection` says which of them are ticked, so the write hits
	// the operator's checkboxes and never the page they happened to be on. The
	// response is the number of Features changed. The page watches `error` and
	// toasts it, so a failure is reported and the rows are left as they were
	// (nothing is reloaded on the failing path).
	async function setPlannedRevisit(
		selection: MapReviewBulkSelection,
		revisitAt: string | null,
	): Promise<number> {
		flushPendingLoad();
		try {
			const request: MapReviewPlannedRevisitRequest = {
				spec: spec.value,
				selection,
				revisit_at: revisitAt,
			};
			const changed = await apiClient.JSONPut<number>(
				"/api/feature/revisit",
				request,
			);
			await load();
			return changed;
		} catch (err) {
			error.value = apiErrorMessage(
				err,
				"Failed to save the planned revisit date",
			);
			errorTitle.value = "Planned revisit";
			throw err;
		}
	}

	// --- The ledger's checkbox selection -------------------------------------

	/** One table's selection, never shared across tables: a set of Sites and a
	 * set of Features answer different actions, so mixing them would leave the
	 * panel with no single verb. */
	function ledgerSelectionFor(t: MapReviewTable): LedgerSelection {
		return ledgerSelections.value[t] ?? EMPTY_LEDGER_SELECTION;
	}

	function clearLedgerSelection(t?: MapReviewTable): void {
		if (t === undefined) {
			ledgerSelections.value = {};
			return;
		}
		const next = { ...ledgerSelections.value };
		delete next[t];
		ledgerSelections.value = next;
	}

	/** Whether one row is ticked, under either selection mode. */
	function isRowChecked(t: MapReviewTable, id: number): boolean {
		const selection = ledgerSelectionFor(t);
		return selection.all
			? !selection.excluded.includes(id)
			: selection.ids.includes(id);
	}

	/** How many rows the selection covers. In the all-matching mode the count is
	 * the filter's own total for that table, so it stays honest across pages;
	 * an explicit set is as long as the operator made it. */
	function ledgerSelectionCountFor(t: MapReviewTable): number {
		const selection = ledgerSelectionFor(t);
		if (!selection.all) return selection.ids.length;
		const keepCount =
			t === activeTable.value ? total.value : (counts.value[t] ?? 0);
		return Math.max(0, keepCount - selection.excluded.length);
	}

	/** Toggle one row. In the all-matching mode an untick becomes an exclusion,
	 * so the selection still means "everything except these" rather than
	 * collapsing to the page the operator happens to be on. */
	function toggleRowChecked(t: MapReviewTable, id: number): void {
		const current = ledgerSelectionFor(t);
		if (current.all) {
			const excluded = current.excluded.includes(id)
				? current.excluded.filter((other) => other !== id)
				: [...current.excluded, id];
			ledgerSelections.value = {
				...ledgerSelections.value,
				[t]: { all: true, ids: [], excluded },
			};
			return;
		}
		const ids = current.ids.includes(id)
			? current.ids.filter((other) => other !== id)
			: [...current.ids, id];
		ledgerSelections.value = {
			...ledgerSelections.value,
			[t]: { all: false, ids, excluded: [] },
		};
	}

	/** The header checkbox. A full all-matching selection clears; anything else
	 * (nothing, some rows, or all-with-exclusions) becomes the full set. */
	function toggleSelectAllMatching(t: MapReviewTable): void {
		const current = ledgerSelectionFor(t);
		if (current.all && current.excluded.length === 0) {
			clearLedgerSelection(t);
			return;
		}
		ledgerSelections.value = {
			...ledgerSelections.value,
			[t]: { all: true, ids: [], excluded: [] },
		};
	}

	// The active table's selection, as the ledger and the actions panel read it.
	const ledgerSelection = computed<LedgerSelection>(() =>
		ledgerSelectionFor(activeTable.value),
	);
	const ledgerSelectionCount = computed(() =>
		ledgerSelectionCountFor(activeTable.value),
	);
	/** The one row ticked when exactly one is: the Details panel shows that
	 * record rather than a summary of one. An explicit set names it directly; an
	 * all-matching selection narrowed to one names it through the page, which is
	 * the only place the client holds its id. `null` whenever the count is not
	 * exactly one, so nothing here guesses. */
	const checkedSingleID = computed<number | null>(() => {
		const selection = ledgerSelection.value;
		if (!selection.all) {
			return selection.ids.length === 1 ? selection.ids[0] : null;
		}
		if (ledgerSelectionCount.value !== 1) return null;
		const row = rows.value.find(
			(candidate) => !selection.excluded.includes(candidate.id),
		);
		return row ? row.id : null;
	});

	// The selection a bulk write is narrowed to. `{}` states the whole spec's
	// keep set, which is exactly what an untampered all-matching selection is.
	const bulkSelection = computed<MapReviewBulkSelection>(() => {
		const selection = ledgerSelection.value;
		if (!selection.all) return { ids: [...selection.ids] };
		return selection.excluded.length > 0
			? { exclude_ids: [...selection.excluded] }
			: {};
	});

	// A filter change redefines what "all matching" means, so no selection can
	// survive it: the Sites a set named may no longer be the rows on screen, and
	// a stale selection with a live write button is worse than starting over.
	watch(
		spec,
		() => {
			ledgerSelections.value = {};
		},
		{ deep: true },
	);

	// Bulk-assign the selected Sites to one technician on one day, in ONE
	// request. The spec says which Sites the table is showing; the selection says
	// which of them are ticked. Each Site ends up with a new assignment that
	// supersedes any prior one — the server keeps the history and the ledger
	// shows only the latest — and the response is the number of Sites written.
	// The ledger reloads so every row shows the server's answer. A failure
	// reports through `error`/`errorTitle`, which the page toasts, and leaves the
	// rows as they were (nothing is reloaded on the failing path).
	async function assignSites(
		selection: MapReviewBulkSelection,
		userID: number,
		assignedOn: string,
		kind: string,
	): Promise<number> {
		flushPendingLoad();
		try {
			const request: MapReviewSiteAssignmentRequest = {
				spec: spec.value,
				selection,
				user_id: userID,
				assigned_on: assignedOn,
				kind,
			};
			const result = await apiClient.JSONPost<MapReviewSiteAssignmentResult>(
				"/api/site-assignment",
				request,
			);
			await load();
			return result.sites;
		} catch (err) {
			error.value = apiErrorMessage(err, "Failed to assign the sites");
			errorTitle.value = "Site assignment";
			throw err;
		}
	}

	// Cancel the current assignment of the selected Sites, symmetric with the
	// assign above: the same spec and selection, and the count of Sites written
	// back. The ledger then shows none of them assigned; the cancelled plan is
	// kept in the history, not deleted. A Site that had no assignment is not a
	// failure — the write simply changed nothing for it.
	async function clearSiteAssignments(
		selection: MapReviewBulkSelection,
	): Promise<number> {
		flushPendingLoad();
		try {
			const request: MapReviewSiteAssignmentClearRequest = {
				spec: spec.value,
				selection,
			};
			const result = await apiClient.JSONPost<MapReviewSiteAssignmentResult>(
				"/api/site-assignment/clear",
				request,
			);
			await load();
			return result.sites;
		} catch (err) {
			error.value = apiErrorMessage(
				err,
				"Failed to clear the site assignments",
			);
			errorTitle.value = "Site assignment";
			throw err;
		}
	}

	// Apply and withdraw tags on the selected Sites, in ONE request. Removals
	// free a Site's tag budget before additions are counted, so swapping one tag
	// for another works on a Site already at the cap; a batch that would still
	// overflow is refused whole with the Sites named, and that 409 is the
	// operator's message. The ledger reloads so its row chips are the server's
	// answer.
	async function assignSiteTags(
		selection: MapReviewBulkSelection,
		add: number[],
		remove: number[],
	): Promise<SiteTagAssignResult> {
		flushPendingLoad();
		if (add.length === 0 && remove.length === 0) {
			// Nothing chosen is nothing to send; saying so is better than posting
			// an empty batch the server would accept and change nothing with.
			error.value = "Choose a tag to apply or a tag to remove.";
			errorTitle.value = "Site tags";
			throw new Error("no tag chosen");
		}
		try {
			const request: SiteTagAssignRequest = {
				spec: spec.value,
				selection,
				add,
				remove,
			};
			const result = await apiClient.JSONPost<SiteTagAssignResult>(
				"/api/site-tag/assign",
				request,
			);

			await load();
			return result;
		} catch (err) {
			error.value = apiErrorMessage(err, "Failed to apply the site tags");
			errorTitle.value = "Site tags";
			throw err;
		}
	}

	// VectorSurv filing capture: the bulk action's rig picker. The list is the
	// district's own equipment, read once here and shared with the treatment
	// detail panel's picker.
	const filingEquipment = ref<VectorSurvEquipment[]>([]);
	const filingEquipmentError = ref("");
	const filingLoading = ref(false);
	const filingEquipmentID = ref<number | null>(null);

	async function loadFilingEquipment(): Promise<void> {
		if (filingLoading.value) return;
		filingLoading.value = true;
		filingEquipmentError.value = "";
		try {
			filingEquipment.value = await apiClient.JSONGet<VectorSurvEquipment[]>(
				"/api/integration/vectorsurv/equipment",
			);
		} catch (err) {
			filingEquipmentError.value = apiErrorMessage(
				err,
				"Failed to load the equipment list",
			);
		} finally {
			filingLoading.value = false;
		}
	}

	function setFilingEquipment(id: number | null): void {
		filingEquipmentID.value = id;
	}

	// Annotate the selected treatments with the chosen rig, in ONE request. The
	// spec says which treatments the grid is showing and the selection says
	// which of them are ticked, exactly as the ledger's other bulk writes do, so
	// the selection narrows the spec and can never reach a row the grid did not
	// show. The response is the number annotated; the ledger then reloads so the
	// filing state shows beside the rows. A failure reports through the store's
	// error channel and leaves the rows untouched.
	async function annotateTreatments(
		selection: MapReviewBulkSelection,
	): Promise<number> {
		flushPendingLoad();
		const equipmentId = filingEquipmentID.value;
		if (equipmentId === null) {
			error.value = "Choose an equipment before recording the filing capture.";
			errorTitle.value = "VectorSurv filing";
			throw new Error("no equipment chosen");
		}
		try {
			const request: VectorSurvFilingAnnotationRequest = {
				spec: spec.value,
				selection,
				equipmentId,
				applicatorId: null,
			};
			const result = await apiClient.JSONPost<VectorSurvFilingAnnotationResult>(
				"/api/integration/vectorsurv/filing/annotation",
				request,
			);
			await load();
			return result.treatments;
		} catch (err) {
			error.value = apiErrorMessage(
				err,
				"Failed to record the VectorSurv filing capture",
			);
			errorTitle.value = "VectorSurv filing";
			throw err;
		}
	}

	// The distribution of a numeric column under the current spec, for the
	// min/max filter's bars. Cached per (table, column, spec-without-its-own-
	// clause): dragging that filter's window must not change the bars, and the
	// endpoint strips the column's own clause for the same reason, so the key
	// ignores it too. Reading it is a couple of hundred milliseconds, so the
	// cache also keeps a reopened panel instant.
	const histograms = ref<Record<string, MapReviewHistogramResponse>>({});

	// The district's live trap vocabularies, resolved once for the grid's
	// vocabulary columns and the counts clause's species and sex controls, and the
	// lab's three test vocabularies beside them for the Pools ledger's columns. The
	// server's registry declares those columns with no static member set — an id
	// matching no row keeps no row — so the options an operator can pick come
	// from here rather than from the column registry.
	const vocabulary = ref<MapReviewTrapVocabulary | null>(null);
	async function loadVocabulary(): Promise<void> {
		if (vocabulary.value) return;
		try {
			vocabulary.value = await apiClient.JSONGet<MapReviewTrapVocabulary>(
				"/api/trap-vocabulary",
			);
		} catch {
			vocabulary.value = {
				species: [],
				sex: [],
				trap_type: [],
				lure: [],
				test_method: [],
				test_target: [],
				test_status: [],
				// The products vocabulary belongs to the same registry as the
				// seven above; an empty fallback keeps every column's option
				// list empty rather than leaving the object short a key.
				products: [],
			};
		}
	}

	async function histogram(
		table: MapReviewTable,
		column: string,
	): Promise<MapReviewHistogramResponse | null> {
		// The column's own clause is dropped: the bars show the whole
		// distribution, so dragging the window moves the selection rather than
		// collapsing the shape. The endpoint applies the same rule.
		const withoutOwnClause: MapReviewSpec = {
			...spec.value,
			filters: spec.value.filters.filter(
				(f) => !(f.table === table && f.column === column),
			),
		};
		// The domain travels with the request so the server counts into the same
		// scale the client draws on, and it comes from the column's own registry
		// entry — one place, not two. A column without one has no slider, so it
		// has no bars either.
		const domain = columnByKey(table, column)?.rangeDomain;
		if (!domain) return null;
		const key = `${table}.${column}:${JSON.stringify(withoutOwnClause)}`;
		const cached = histograms.value[key];
		if (cached) return cached;
		// The wire narrowing needs each property's declared type (a boolean
		// member, a measure, an id-backed enum), which `ledgerFilter` reads from
		// the capability document — the same translation the ledger's own page,
		// counts and footer go through.
		await loadCapabilities();
		try {
			const response = await apiClient.JSONPost<MapReviewHistogramResponse>(
				"/api/histogram",
				{
					resource: table,
					column,
					bins: 24,
					domain_min: domain.min,
					domain_max: domain.max,
					log_scale: domain.log,
					filter: ledgerFilter(table, withoutOwnClause, capabilities.value),
					location: ledgerLocation(withoutOwnClause),
					tz_offset_minutes: ledgerTZOffset(table, withoutOwnClause),
				} satisfies MapReviewHistogramRequest,
			);
			histograms.value = { ...histograms.value, [key]: response };
			return response;
		} catch {
			// A column without a server-side histogram (an older backend, or a
			// column that is not numeric) simply draws no bars; the filter
			// itself still works, so this is not worth surfacing as an error.
			return null;
		}
	}

	// ask turns a plain-English question into a spec on the server, applies it
	// to the shared spec, and shows the matching page. A generated spec
	// REPLACES the ledger's filter — the question is the whole request, so
	// composing it with a stale filter would show records the operator did not
	// ask for. An unanswerable question leaves the current view untouched and
	// returns the explanation for the caller to show; only a transport or
	// server fault throws.
	async function ask(question: string): Promise<MapReviewAskResponse> {
		flushPendingLoad();
		try {
			const reply = await apiClient.JSONPost<MapReviewAskResponse>("/api/ask", {
				question,
				tz_offset_minutes: clientZoneOffsetMinutes(),
			} satisfies MapReviewAskRequest);
			if (!reply.answered) return reply;
			// The search map is filled out for every table so the store's own
			// accesses never read an absent key.
			spec.value = {
				// The question is the whole request, so its zone narrowing
				// replaces the operator's; a spec from a server that predates
				// zones carries none.
				zones: reply.spec.zones ?? [],
				// The spatial clause's buffer rides in the answer this way: the
				// server materialised the subject's buffer and appended it to
				// the target's spec, so the map draws the shape the answer was
				// computed inside. A spec from a server that predates temp
				// zones carries none.
				temp_zones: reply.spec.temp_zones ?? [],
				// The question may have asked for the buffers' intersection; a
				// spec from a server that predates the mode reads as union.
				buffer_combine: reply.spec.buffer_combine ?? "union",
				search: {
					site: reply.spec.search?.site ?? "",
					feature: reply.spec.search?.feature ?? "",
					treatment: reply.spec.search?.treatment ?? "",
					observation: reply.spec.search?.observation ?? "",
					trap_station: reply.spec.search?.trap_station ?? "",
					trap_instance: reply.spec.search?.trap_instance ?? "",
					species_count: reply.spec.search?.species_count ?? "",
					trap_sample: reply.spec.search?.trap_sample ?? "",
					zone: reply.spec.search?.zone ?? "",
					pool: reply.spec.search?.pool ?? "",
				},
				filters: reply.spec.filters ?? [],
			};
			// The ask's table must be one this surface reads. A question answered
			// about any other (the lab reads two of the registry's tables) leaves
			// the grid where it is: the surface has no ledger for that table, so
			// its page cannot be shown here.
			const answered = reply.table ?? activeTable.value;
			const adopts = ledgerTables.value.includes(answered);
			if (adopts) activeTable.value = answered;
			page.value = 0;
			// The server already resolved the spec and returned the page, so the
			// rows are taken from it rather than re-queried: one request, and the
			// table shows exactly what the ask resolved.
			if (reply.response && adopts) {
				rows.value = reply.response.rows;
				total.value = reply.response.total;
				counts.value = reply.response.counts;
				mapSelection.value = reply.response.map;
				overRateIDs.value = reply.response.over_rate_ids ?? [];
				overRateExact.value = reply.response.over_rate_exact;
			} else {
				await load();
			}
			return reply;
		} catch (err) {
			error.value = apiErrorMessage(err, "Failed to answer the question");
			errorTitle.value = "Question failed";
			throw err;
		}
	}

	return {
		// State
		spec,
		activeTable,
		// The ledger tables this surface reads, pinned on setup by the page that
		// owns the surface (the review workbench pins the whole registry).
		ledgerTables,
		selection,
		rows,
		total,
		counts,
		// The last successful query's per-column footer aggregates.
		summary,
		mapSelection,
		// The server's over-rate set, read by the map's over-rate-only toggle.
		overRateIDs,
		overRateExact,
		page,
		limit,
		sort,
		loading,
		error,
		errorTitle,
		// The last ledger round's outcome; `counts` is only meaningful in the
		// `resolved` state (see the declaration).
		loadState,
		// The deadline a timed-out round hit, in seconds; null for any other
		// failure.
		timedOutSeconds,
		drawerOpen,
		// Whether the active ledger's typed filter row is showing, toggled from
		// the Filters tool in the right rail.
		filtersOpen,
		// The ledger's height, shared with the rails that stop above it.
		drawerFraction,
		drawerExpanded,
		// The active table's checkbox selection, the count it covers, the one
		// explicitly ticked row, and the wire shape a bulk write is narrowed to.
		// The drawing mode every armed draw tool reads sits beside them.
		ledgerSelection,
		ledgerSelectionCount,
		checkedSingleID,
		bulkSelection,
		drawMode,
		// The report: its aggregate and its load state, for the Analysis pane.
		// `dashboard` is null until the first successful fetch.
		dashboard,
		dashboardLoading,
		// The surveillance section: the trap family's weekly abundance against a
		// five-year baseline. `surveillance` is null until the first fetch.
		surveillance,
		surveillanceLoading,
		// The population view: its payload and load state, whether its cells are
		// on the workbench map, whether that fill is showing, and the bivariate
		// class in force. The Analysis pane is the payload's caller; the map
		// reads it through `populationOnMap`, and the Layers pane's checkbox
		// writes `populationVisible`.
		population,
		populationLoading,
		populationOnMap,
		populationVisible,
		populationClass,
		// The waste view: its payload and load state, whether its cells are on
		// the workbench map, whether that fill is showing, the bivariate class
		// in force, and the re-treatment bucket in force. The Analysis pane is
		// the payload's caller; the map reads it through `wasteOnMap`, and the
		// Layers pane's checkbox writes `wasteVisible`.
		waste,
		wasteLoading,
		wasteOnMap,
		wasteVisible,
		wasteClass,
		wasteBucket,
		// The map control pane's legend counts over the district's whole Feature
		// set, from `POST /api/feature-counts`.
		featureCounts,
		// Computed
		filtersActive,
		activeColumnFilters,
		// The applied-filter indicator's clause list and whether anything
		// narrows at all.
		appliedFilters,
		specNarrowed,
		// Trap vocabularies, for the vocabulary columns and the counts clause.
		vocabulary,
		// The ledger's columns with the server's declared capability folded on
		// (which columns sort, which operators and static members they carry), and
		// the load that reads each resource's document.
		columnsFor,
		loadCapabilities,
		// Actions
		load,
		fetchRow,
		fetchStationInstances,
		setActiveTable,
		setLedgerTables,
		setSearch,
		setFilter,
		clearFilters,
		// The Filters pane's per-row remove: one applied item out, the rest of
		// the spec in force.
		clearApplied,
		histogram,
		loadVocabulary,
		followRelation,
		setDrawMode,
		setZones,
		setTempZones,
		addTempZone,
		clearTempZones,
		setTempZoneInside,
		setBufferCombine,
		setSort,
		setPage,
		select,
		clearSelection,
		openDrawer,
		closeDrawer,
		setDrawerExpanded,
		setDrawerFraction,
		toggleFiltersOpen,
		hydrate,
		loadDashboard,
		loadSurveillance,
		loadPopulation,
		sendPopulationToMap,
		togglePopulationVisible,
		togglePopulationClass,
		loadWaste,
		sendWasteToMap,
		toggleWasteVisible,
		toggleWasteClass,
		selectWasteBucket,
		filterByObserver,
		loadFeatureCounts,
		drillDown,
		setPlannedRevisit,
		assignSites,
		clearSiteAssignments,
		assignSiteTags,
		// The ledger's checkbox selection: read a row's state, toggle one row,
		// toggle the whole filtered set, and clear a table's set.
		isRowChecked,
		toggleRowChecked,
		toggleSelectAllMatching,
		clearLedgerSelection,
		// VectorSurv filing capture: the rig list the bulk action and the
		// treatment detail panel share, and the bulk write they make.
		filingEquipment,
		filingEquipmentError,
		filingLoading,
		filingEquipmentID,
		loadFilingEquipment,
		setFilingEquipment,
		annotateTreatments,
		ask,
	};
});
